import { InputContainer, MarkdownEditor, TextField } from 'components'
import { lowerCase } from 'lodash-es'
import { useState } from 'react'
import {
  DraftSession,
  FormField,
  MultiLanguageString,
  PrimaryLeadProperty,
  SessionWithLead,
  primaryLeadProperties,
} from 'types'
import FormSection from '../FormSection'

interface Props {
  previewMode?: boolean
  basicFields: FormField[]
  userSession: DraftSession
  registrationConsentInfo?: MultiLanguageString
  setUserSession: (updatedSession: DraftSession) => void
}

export default function BasicQuestionsSection({
  previewMode,
  basicFields,
  userSession,
  registrationConsentInfo,
  setUserSession,
}: Props) {
  const [errors, setErrors] = useState<Record<string, string | undefined>>({})

  function handleValueSelection(value: string, property: string) {
    const primaryLeadProp = primaryLeadProperties.includes(property as PrimaryLeadProperty)
    const targetLead = primaryLeadProp ? { ...userSession?.lead } : { ...userSession?.lead?.custom }

    if (value === '') delete (targetLead as any)[property]
    else (targetLead as any)[property] = value

    if (primaryLeadProp)
      setUserSession({
        ...userSession,
        lead: targetLead,
      })
    else
      setUserSession({
        ...userSession,
        lead: {
          ...userSession?.lead,
          custom: targetLead,
        },
      } as SessionWithLead)
  }

  return (
    <FormSection key="basic-questions" previewMode={previewMode}>
      {basicFields.map(field => {
        const currentValue =
          field.property in (userSession.lead ?? {}) ||
          field.property in (userSession.lead?.custom ?? {})
            ? (userSession.lead as any)[field.property] ||
              (userSession.lead?.custom as any)[field.property]
            : undefined

        return (
          <InputContainer label={field.label.en} key={field.property}>
            <TextField
              value={currentValue || ''}
              onChange={e => {
                const value = e.target.value
                handleValueSelection(value, field.property)
              }}
              onBlur={e => {
                const value = e.target.value
                const { min, max } = field
                setErrors(prev => ({
                  ...prev,
                  [field.property]: isValidInput(value, min, max),
                }))
              }}
              placeholder={`Enter ${lowerCase(field.label.en)}...`}
              required={field.required}
              isError={!!errors[field.property]}
              helperText={errors[field.property]}
            />
          </InputContainer>
        )
      })}

      {registrationConsentInfo && (
        <MarkdownEditor
          value={registrationConsentInfo?.en || ''}
          readOnly
          containerProps={{
            maxHeight: '100px',
          }}
        />
      )}
    </FormSection>
  )
}

function isValidInput(value: string, minLength?: number, maxLength?: number) {
  const length = value.trim().length
  if (minLength !== undefined && maxLength !== undefined && minLength === maxLength) {
    if (length !== minLength) return `Value must be exactly ${minLength} characters long`
  } else if (
    minLength !== undefined &&
    maxLength !== undefined &&
    (length < minLength || length > maxLength)
  )
    return `Value must be between ${minLength} and ${maxLength} characters long`
  else if (minLength !== undefined && length < minLength)
    return `Value must be at least ${minLength} characters long`
  else if (maxLength !== undefined && length > maxLength)
    return `Value must be no more than ${maxLength} characters long`

  return undefined
}
